<template>
  <div class="sonar-overview-card">
    <div
      v-if="isShareModalOpen"
      class="sonar-overview-card__share-modal"
      @mouseleave="handleShareModal"
    >
      <div
        class="sonar-overview-card__share-option-wrapper"
        @click="linkedInUrl()"
      >
        <LinkedinIcon class="sonar-overview-card__share-option-icon" />
        <div class="sonar-overview-card__share-option-label">
          Share on LinkedIn
        </div>
      </div>
      <div
        v-tooltip="{
          show: tooltipCopied,
          trigger: 'click',
          content: tooltipText,
          placement: 'top-center',
          hideOnTargetClick: false,
        }"
        class="sonar-overview-card__share-option-wrapper"
        @click.stop="copyToClipboard"
        @mouseout="tooltipCopied = false"
      >
        <LinkIcon class="sonar-overview-card__share-option-icon" />
        <div class="sonar-overview-card__share-option-label">
          Copy link
        </div>
      </div>
    </div>
    <div v-if="!isSharingResults" class="sonar__header-wrapper">
      <h3 class="sonar__header-sub-title">
        My scorecard
      </h3>
      <SonarTabs v-if="userHasStats" />
    </div>

    <div
      :class="[
        'ine-card sonar-overview-card__wrapper',
        isResultsModal && 'sonar-overview-card--results',
      ]"
    >
      <img
        src="../assets/images/score-bg.png"
        alt="Score background"
        class="sonar-overview-card__bg"
      />
      <div
        v-if="!isSharingResults"
        ref="shareBtn"
        v-tooltip="{
          content: 'Share scorecard',
          placement: 'top-center',
        }"
        class="sonar-overview-card__share-wrapper"
        @click="handleOptionsTooltip"
      >
        <ShareIcon class="sonar-overview-card__share-icon" />
      </div>
      <SonarCard
        :currentScore="currentScore"
        :isResultsModal="isResultsModal"
        :showStatsCTA="false"
      />
      <SonarSkillsProgress
        v-bind="{
          currentScore,
          isResultsModal,
          isSharingResults,
        }"
      />
    </div>
  </div>
</template>

<script>
import SonarCard from '@/sonar/components/SonarCard.vue'
import SonarTabs from '@/sonar/components/SonarTabs.vue'
import SonarSkillsProgress from '@/sonar/components/SonarSkillsProgress.vue'
import { ShareIcon, LinkedinIcon, LinkIcon } from 'vue-feather-icons'
import { buildLinkedinLink } from '@/helpers/externalLinks'

export default {
  name: 'SonarOverviewCard',
  components: {
    SonarCard,
    SonarTabs,
    SonarSkillsProgress,
    ShareIcon,
    LinkedinIcon,
    LinkIcon,
  },
  props: {
    currentScore: {
      type: Object,
      required: true,
    },
    isResultsModal: {
      type: Boolean,
      required: false,
    },
    isSharingResults: {
      type: Boolean,
      required: false,
      default: false,
    },
    userHasStats: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isShareModalOpen: false,
      score_id: null,
      tooltipCopied: false,
    }
  },
  computed: {
    scoreURL() {
      return `${window.location.origin}/sonar-share?id=${this.score_id}`
    },
    tooltipText() {
      return this.tooltipCopied ? 'Link copied' : ''
    },
  },
  methods: {
    handleOptionsTooltip() {
      this.isShareModalOpen = !this.isShareModalOpen
    },
    handleShareModal() {
      if (this.isShareModalOpen) this.isShareModalOpen = false
    },

    async getScoreId() {
      this.score_id = await this.$store.dispatch(
        'sonar/getScoringIdByLearningArea',
        this.currentScore.id
      )
    },
    async linkedInUrl() {
      if (!this.score_id) await this.getScoreId()
      if (this.score_id) window.open(buildLinkedinLink(this.scoreURL), '_blank')
      return (this.isShareModalOpen = false)
    },
    async copyToClipboard() {
      if (!this.score_id) await this.getScoreId()
      try {
        await navigator.clipboard.writeText(this.scoreURL)
        this.tooltipCopied = true
      } catch (err) {
        this.$store.dispatch(
          'app/setError',
          "Your browser doesn't allow clipboard access."
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-overview-card {
  color: var(--white);
  &__wrapper {
    position: relative;
    width: 100%;
    display: flex;
    border-radius: 10px;
    overflow: hidden;
    flex-direction: column;
    align-items: center;
    @include tablet {
      flex-direction: row;
      min-height: 468px;
    }
  }
  &__bg {
    position: absolute;
    left: 0;
    top: 0;
  }
  &__share {
    &-wrapper {
      position: absolute;
      right: 30px;
      top: 30px;
      padding: 13px 15px;
      border-radius: 8px;
      z-index: 200;
      cursor: pointer;
      height: 48px;
      &:hover {
        background: var(--white-10);
      }
      &--opened {
        background: var(--white-20);
      }
      @include tablet {
        top: 42px;
      }
    }
    &-icon {
      width: 18px;
      height: 22px;
      color: var(--white);
    }
    &-modal {
      position: absolute;
      right: 40px;
      top: 190px;
      z-index: 300;
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 193px;
      height: 100px;
      border-radius: 10px;
      background: var(--glass-mid-gray), var(--dark-gray);
      z-index: 1000;
      &::after {
        content: ' ';
        position: absolute;
        top: 100%;
        left: 85%;
        border-color: var(--menu-background) transparent transparent transparent;
        transform: translateX(50%);
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        @include mobile {
          left: 50%;
          border-color: var(--darker-gray) transparent transparent transparent;
        }
      }
      @include mobile {
        right: 63px;
        top: 240px;
      }
    }
  }
  &__share-option {
    &-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--white);
      padding: 10px 20px;
      cursor: pointer;
      &:hover {
        background: var(--glass-extra-light);
      }
    }
    &-icon {
      width: 18px;
      height: 18px;
    }
  }
  &--results {
    background: var(--glass-mid-gray), var(--dark-gray);
    @include mobile {
      width: 978px;
      height: 468px;
    }
  }
}
</style>
